/**
 * @file index.ts
 *
 * @fileoverview Exports job schema.org utils.
 */

import addDays from 'date-fns/addDays';
import { JobPosting, MonetaryAmount } from 'schema-dts';
import { JobApplicationWorkflowEnum } from '~/jobiqo/components/JobApplication/enums';
import { getCurrentFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { Job, Price } from '~/jobiqo/types/types';

type GetDirectApplyReturnType = 'True' | 'False' | null;

/**
 * Calculates directApply property for schema.org annotations.
 * @param {Job} job
 *  The job.
 * @returns {GetDirectApplyReturnType}
 *  Return the value depending on the business rules.
 */
export function getDirectApply({
  applicationWorkflow,
}: Job): GetDirectApplyReturnType {
  const directApply =
    getCurrentFrontendConfig().JOBS?.SCHEMA_ORG?.DIRECT_APPLY ?? true;
  // If the jobiqo site has a login wall, set directApply to FALSE.
  if (!directApply) {
    return 'False';
  } else {
    // If application system is "external" , don't add the property directApply
    // at all, we can't fully trust the external source and therefore we can
    // neither set TRUE nor FALSE.
    if (applicationWorkflow === JobApplicationWorkflowEnum.EXTERNAL) {
      return null;
    } else {
      // If the job has application system "internal" or "none", set
      // directApply to TRUE.
      if (
        applicationWorkflow === JobApplicationWorkflowEnum.INTERNAL ||
        applicationWorkflow === JobApplicationWorkflowEnum.NONE
      ) {
        return 'True';
      }
    }

    // Fallback to the DIRECT_APPLY config, which is true here.
    return 'True';
  }
}

/**
 * Calculate the expiration date with several fallback rules for when the
 * job has a past expiration date.
 *
 * @param {Job} job
 *   The job to check dates for.
 * @returns {string}
 *   The date string as (ISO 8601 as expected by the schema.org definition).
 */
export function getExpirationDate(job: Job): string {
  if (job.expiration) {
    // If there is a expiration date just use that.
    return job.expiration;
  }

  // Calculate all fallbacks for expiration date (30 days, then 60 days then 120 days).
  const publishedPlus30Days = addDays(new Date(job.published), 30);
  const publishedPlus60Days = addDays(new Date(job.published), 60);
  const publishedPlus365Days = addDays(new Date(job.published), 365);
  const now = new Date();
  const expirationByPublishedDate =
    publishedPlus30Days > now
      ? publishedPlus30Days
      : publishedPlus60Days > now
      ? publishedPlus60Days
      : publishedPlus365Days;

  // If 365 days is still in the past, add 30 days to today.
  if (expirationByPublishedDate < now) {
    return addDays(now, 30).toISOString();
  }

  return expirationByPublishedDate.toISOString();
}

/**
 * Constructs baseSalary job posting schema based on
 * on the business rules.
 * @param {Job} job
 * The job.
 * @returns {JobPosting['baseSalary']}
 * BaseSalary job posting schema
 */
export function getBaseSalary({
  schemaOrgSalary,
}: Job): JobPosting['baseSalary'] {
  const parsePrice = (price: Price) => parseFloat(price.number);

  const baseSalary: JobPosting['baseSalary'] = {
    '@type': 'MonetaryAmount',
  };

  const baseSalaryValue: MonetaryAmount['value'] = {
    '@type': 'QuantitativeValue',
    unitText: '',
    value: '',
  };

  if (schemaOrgSalary?.defaultCurrency) {
    baseSalary.currency = schemaOrgSalary.defaultCurrency;
  }

  if (schemaOrgSalary?.unitText) {
    baseSalaryValue.unitText = schemaOrgSalary.unitText;
  }
  if (schemaOrgSalary?.exactValue) {
    baseSalaryValue.value = parsePrice(schemaOrgSalary.exactValue);
  } else {
    if (schemaOrgSalary?.minValue && schemaOrgSalary.maxValue) {
      baseSalaryValue.minValue = parsePrice(schemaOrgSalary.minValue);
      baseSalaryValue.maxValue = parsePrice(schemaOrgSalary.maxValue);
    }
  }

  baseSalary.value = baseSalaryValue;

  return baseSalary;
}

/**
 * Check if the job data is available.
 * The data can sometimes be unavailable,
 * e.g job imported, or job content extraction not fully working
 * @param {Job} job - The job to check
 * @returns {boolean} - True if the job data is available,
 * false otherwise.
 */
export function isRequiredDataAvailable(job: Job): boolean {
  // Check if the job description contains any text,
  // expect for HTML tags.
  const text = job?.description?.replace(/<[^>]*>?/gm, '');
  if (!text) {
    return false;
  }

  return !!(job.title && job.organization);
}
